import { isFreeShippingCountry } from "@/libs/shipping/own-checkout-shipping";

export interface Country {
  countryCallingCodes: string[];
  countryCode: string;
  englishCountryName: string;
  koreanCountryName: string;
}

export interface ShippingPriceDetail {
  weight: number;
  discountRate: number;
  toDollars: number;
  originalPrice: number;
  importHandlingFee: number;
}

export interface ShippingPriceResult {
  courier: string | null;
  price: number;
  detail: ShippingPriceDetail | null;
  estimateShippingTime: number | null;
}

class ShippingApi {
  static calculateShippingPriceBySku = async (
    countryCode: string,
    items: { sku: string; qty: number }[],
    options?: { state?: string; zip?: string; currency?: string },
  ): Promise<ShippingPriceResult | null> => {
    // 한국은 무료 배송처리 — 판정은 GMC 피드와 공용(`isFreeShippingCountry`)
    if (isFreeShippingCountry(countryCode)) {
      return {
        courier: null,
        price: 0,
        detail: null,
        estimateShippingTime: null,
      };
    }

    // trailing slash 필수 — next.config `trailingSlash:true` 라 slash 없으면 308 redirect
    // (POST 재전송 왕복 추가 → 첫 카트 오픈 시 배송비 지연 → 체크아웃 버튼 disabled 장기화).
    // classify-by-sku·Eximbay 와 동일 클래스(docs/pitfalls.md).
    const res = await fetch("/api/shops/shipping-price-by-sku/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        countryCode,
        items,
        state: options?.state,
        zip: options?.zip,
        // 배송비를 카트 통화로 받기 위해 함께 보낸다. 안 보내면 서버가 USD 로 간주하고,
        // 그 숫자가 EUR/GBP 합계에 섞이면 배송비만 과다청구된다(리뷰 §2).
        currency: options?.currency,
      }),
    });

    if (res.ok) {
      return (await res.json()) as ShippingPriceResult;
    }

    // ★실패를 조용히 삼키지 않는다. 종전엔 `null` 만 돌려줘서 카트에 배송비가 `-` 로 뜨고
    // 체크아웃 버튼이 회색이 되는데 **이유를 알 방법이 없었다**.
    //
    // `null` 반환 자체는 유지한다 — reject 로 바꾸면 `useShippingPriceBySku` 가 `isError` 가 되고
    // `filled-content` 가 "Failed to calculate shipping price. Please try again later." 모달을
    // 띄우며 홈으로 보낸다. 501(통화 미지원)은 **재시도해도 안 되는 영구 조건**이라 그 모달이
    // 카트를 열 때마다 반복되어 더 나쁘다.
    //
    // 501 = 서버가 그 통화로 배송비를 만들 수 없다는 뜻(mgmt `calculate-by-sku` 가 USD 만 응답).
    // 근본 해결은 mgmt 통화 인지(admin#1445 #3).
    const detail = await res.text().catch(() => "");
    console.error(
      `[shipping] calculate-by-sku failed: HTTP ${res.status} (country=${countryCode}, currency=${options?.currency ?? "USD"}) ${detail}`,
    );

    return null;
  };
}

export default ShippingApi;
